import React, { useState, useEffect } from 'react';
import { jobs as jobsApi } from '../services/api';
import AlertModal from '../components/AlertModal';

const JobLogs = ({ jobId }) => {
  const [logs, setLogs] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastUpdate, setLastUpdate] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const fetchLogs = async () => {
      try {
        console.log(`[JobLogs] Fetching logs for job ${jobId} at ${new Date().toLocaleTimeString()}`);
        const response = await jobsApi.getLogs(jobId);

        if (isMounted) {
          console.log(`[JobLogs] Received logs:`, response.data.logs?.length || 0, 'characters');
          setLogs(response.data.logs || '');
          setError('');
          setLastUpdate(new Date().toLocaleTimeString());
        }
      } catch (err) {
        console.error(`[JobLogs] Error fetching logs:`, err.response?.status, err.response?.data);

        if (isMounted) {
          if (err.response?.status === 400) {
            // Job is not running anymore or VM doesn't exist
            const errorMsg = err.response?.data?.error || 'Job is not running';
            setError(errorMsg);
            // Don't clear logs on 400 - keep showing the last logs we got
          } else {
            setError(err.response?.data?.error || 'Failed to fetch logs');
          }
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    // Fetch logs immediately
    fetchLogs();

    // Poll every 30 seconds
    const interval = setInterval(fetchLogs, 30000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [jobId]);

  if (loading) {
    return (
      <div style={{ marginTop: '16px', padding: '12px', background: '#f8fafc', borderRadius: '8px', fontSize: '14px' }}>
        Loading logs...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ marginTop: '16px', padding: '12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', fontSize: '14px', color: '#991b1b' }}>
        {error}
      </div>
    );
  }

  return (
    <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e2e8f0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <strong style={{ fontSize: '14px', color: '#334155' }}>Live Logs (last 100 lines):</strong>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          {lastUpdate && (
            <span style={{ fontSize: '12px', color: '#64748b' }}>
              Last update: {lastUpdate}
            </span>
          )}
          <span style={{ fontSize: '12px', color: '#64748b' }}>
            Auto-refresh: 30s
          </span>
        </div>
      </div>
      {error && (
        <div style={{
          marginBottom: '8px',
          padding: '8px 12px',
          background: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '6px',
          fontSize: '13px',
          color: '#991b1b'
        }}>
          {error}
        </div>
      )}
      <pre style={{
        background: '#1e293b',
        color: '#e2e8f0',
        padding: '12px',
        borderRadius: '8px',
        fontSize: '13px',
        lineHeight: '1.5',
        overflow: 'auto',
        maxHeight: '400px',
        fontFamily: 'monospace',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        margin: 0
      }}>
        {logs || 'No logs available yet. The VM may still be initializing...\nLogs will appear here once the job starts executing.'}
      </pre>
    </div>
  );
};

const Jobs = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [modal, setModal] = useState({ isOpen: false, type: 'info', title: '', message: '', onConfirm: null });
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, message: '', onConfirm: null });

  useEffect(() => {
    loadJobs();
    // Reload jobs every 30 seconds to get status updates
    const interval = setInterval(loadJobs, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadJobs = async () => {
    try {
      const response = await jobsApi.getAll();
      setJobs(response.data);
    } catch (error) {
      console.error('Failed to load jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredJobs = jobs.filter((job) => {
    if (filter === 'all') return true;
    return job.status === filter;
  });

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return '⏳';
      case 'running': return '⚙️';
      case 'completed': return '✅';
      case 'failed': return '❌';
      case 'cancelled': return '🚫';
      default: return '❓';
    }
  };

  const handleCancelJob = (jobId) => {
    setConfirmModal({
      isOpen: true,
      message: 'Are you sure you want to cancel this job? The VM will be deleted and the job cannot be resumed.',
      onConfirm: async () => {
        try {
          await jobsApi.cancel(jobId);
          setModal({
            isOpen: true,
            type: 'success',
            title: 'Success',
            message: 'Job cancelled successfully'
          });
          loadJobs();
        } catch (error) {
          setModal({
            isOpen: true,
            type: 'error',
            title: 'Error',
            message: error.response?.data?.error || 'Failed to cancel job'
          });
        }
      }
    });
  };

  const handleDeleteJob = (jobId) => {
    setConfirmModal({
      isOpen: true,
      message: 'Are you sure you want to delete this job? This action cannot be undone and the job will be permanently removed from the system.',
      confirmText: 'Yes, Delete Job',
      onConfirm: async () => {
        try {
          await jobsApi.delete(jobId);
          setModal({
            isOpen: true,
            type: 'success',
            title: 'Success',
            message: 'Job deleted successfully'
          });
          loadJobs();
        } catch (error) {
          setModal({
            isOpen: true,
            type: 'error',
            title: 'Error',
            message: error.response?.data?.error || 'Failed to delete job'
          });
        }
      }
    });
  };

  if (loading) {
    return <div className="loading"><div className="spinner"></div></div>;
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1>📋 My Jobs</h1>
        <p>View and manage your Thor Hammer jobs</p>
      </div>

      <div className="card" style={{ marginBottom: '24px' }}>
        {/* Button class explanation (Code Review #32):
          * Active filter button uses .btn-primary (blue) to indicate selected state
          * Inactive buttons use .btn-secondary (gray) for visual hierarchy
          * This follows page-level button styling convention (not modal context)
          * See App.css for button consolidation architecture details
          */}
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <button
            onClick={() => setFilter('all')}
            className={filter === 'all' ? 'btn-primary btn-sm' : 'btn-secondary btn-sm'}
          >
            All ({jobs.length})
          </button>
          <button
            onClick={() => setFilter('pending')}
            className={filter === 'pending' ? 'btn-primary btn-sm' : 'btn-secondary btn-sm'}
          >
            Pending ({jobs.filter(j => j.status === 'pending').length})
          </button>
          <button
            onClick={() => setFilter('running')}
            className={filter === 'running' ? 'btn-primary btn-sm' : 'btn-secondary btn-sm'}
          >
            Running ({jobs.filter(j => j.status === 'running').length})
          </button>
          <button
            onClick={() => setFilter('completed')}
            className={filter === 'completed' ? 'btn-primary btn-sm' : 'btn-secondary btn-sm'}
          >
            Completed ({jobs.filter(j => j.status === 'completed').length})
          </button>
          <button
            onClick={() => setFilter('failed')}
            className={filter === 'failed' ? 'btn-primary btn-sm' : 'btn-secondary btn-sm'}
          >
            Failed ({jobs.filter(j => j.status === 'failed').length})
          </button>
          <button
            onClick={() => setFilter('cancelled')}
            className={filter === 'cancelled' ? 'btn-primary btn-sm' : 'btn-secondary btn-sm'}
          >
            Cancelled ({jobs.filter(j => j.status === 'cancelled').length})
          </button>
        </div>
      </div>

      {filteredJobs.length === 0 ? (
        <div className="card">
          <p style={{ color: '#64748b', textAlign: 'center', padding: '40px' }}>
            {filter === 'all' ? 'No jobs found' : `No ${filter} jobs`}
          </p>
        </div>
      ) : (
        filteredJobs.map((job) => (
          <div key={job.id} className="job-card">
            <div className="job-header">
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <span style={{ fontSize: '20px' }}>{getStatusIcon(job.status)}</span>
                  <h3 style={{ color: '#334155' }}>{job.repository.name}</h3>
                </div>
                <p style={{ color: '#64748b', marginBottom: '12px' }}>{job.command}</p>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', fontSize: '14px', color: '#64748b' }}>
                  <div>
                    <strong>Branch:</strong> {job.branch_name}
                  </div>
                  <div>
                    <strong>Created:</strong> {new Date(job.created_at).toLocaleString()}
                  </div>
                  {job.completed_at && (
                    <div>
                      <strong>Completed:</strong> {new Date(job.completed_at).toLocaleString()}
                    </div>
                  )}
                </div>
              </div>
              <span className={`job-status status-${job.status}`}>
                {job.status}
              </span>
            </div>

            {job.pr_url && (
              <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e2e8f0' }}>
                <a
                  href={job.pr_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-success btn-sm"
                  style={{ textDecoration: 'none' }}
                >
                  🔗 View Pull Request
                </a>
              </div>
            )}

            {job.error_message && (
              <div className="alert alert-error" style={{ marginTop: '16px' }}>
                <strong>Error:</strong> {job.error_message}
              </div>
            )}

            {(job.status === 'running' || job.status === 'pending') && (
              <>
                <div style={{ marginTop: '16px', display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                  <div className="alert alert-info" style={{ flex: 1, margin: 0 }}>
                    <div className="spinner" style={{ display: 'inline-block', width: '16px', height: '16px', marginRight: '8px', verticalAlign: 'middle' }}></div>
                    Job is running... This may take 5-15 minutes. The page will auto-refresh every 30 seconds.
                  </div>
                  <button
                    onClick={() => handleCancelJob(job.id)}
                    className="btn-danger btn-sm"
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    ❌ Cancel Job
                  </button>
                </div>
                {job.status === 'running' && <JobLogs jobId={job.id} />}
              </>
            )}

            {(job.status === 'completed' || job.status === 'failed' || job.status === 'cancelled') && (
              <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => handleDeleteJob(job.id)}
                  className="btn-danger btn-sm"
                  style={{ whiteSpace: 'nowrap' }}
                >
                  🗑️ Delete Job
                </button>
              </div>
            )}
          </div>
        ))
      )}

      <AlertModal
        isOpen={modal.isOpen}
        onClose={() => setModal({ ...modal, isOpen: false })}
        type={modal.type}
        title={modal.title}
        message={modal.message}
        onConfirm={modal.onConfirm}
      />

      <AlertModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        type="confirm"
        message={confirmModal.message}
        confirmText={confirmModal.confirmText || "Yes, Cancel Job"}
        onConfirm={() => {
          setConfirmModal({ ...confirmModal, isOpen: false });
          if (confirmModal.onConfirm) confirmModal.onConfirm();
        }}
      />
    </div>
  );
};

export default Jobs;
